/**
 * A row of mutually exclusive buttons in one frame — the view or cut of a
 * block («Рынок | Команда», «номер звонка | сотрудник | стадия»). Each option is
 * a button with `aria-pressed`, the group is named by `aria-label` or by a
 * visible label's id.
 */
import { cn } from '@/lib/utils';

interface SegmentedButtonsProps<T extends string> {
  options: readonly { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  /** The group's name when there is no visible label. */
  ariaLabel?: string;
  /** The id of the visible label that names the group. */
  labelledBy?: string;
}

export function SegmentedButtons<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  labelledBy,
}: SegmentedButtonsProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      className="inline-flex flex-wrap rounded-button border border-border bg-bg-surface p-0.5"
    >
      {options.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          aria-pressed={value === id}
          className={cn(
            'rounded-button px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === id
              ? 'bg-primary-dim text-primary-fg'
              : 'text-fg-muted hover:text-fg'
          )}
          onClick={() => onChange(id)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
